/**
 * AudioManager — framework-level sound management.
 *
 * Wraps Phaser's sound manager with volume categories (master, music, sfx, ambient),
 * mute controls, and a procedural tone generator for quick prototyping without
 * needing audio files.
 *
 * Stored in game.registry as "audio". Accessible from any scene.
 *
 * Usage:
 *   const audio = this.game.registry.get("audio") as AudioManager;
 *   audio.playSfx("tile_select");
 *   audio.playMusic("battle_theme");
 *   audio.setVolume("sfx", 0.5);
 */

declare const Phaser: any;

export type AudioCategory = "music" | "sfx" | "ambient";

export interface ToneConfig {
  /** Frequency in Hz */
  frequency: number;
  /** Duration in seconds */
  duration: number;
  /** Waveform type */
  type?: OscillatorType;
  /** Volume 0–1 (before category scaling) */
  volume?: number;
  /** Frequency to slide to over duration (for pitch bends) */
  slideTo?: number;
}

export class AudioManager {
  private game: any;
  private masterVolume = 1;
  private categoryVolumes: Record<AudioCategory, number> = {
    music: 0.5,
    sfx: 0.7,
    ambient: 0.4,
  };
  private muted = false;
  private categoryMuted: Record<AudioCategory, boolean> = {
    music: false,
    sfx: false,
    ambient: false,
  };

  /** Registered procedural tones — key → ToneConfig */
  private tones: Map<string, ToneConfig> = new Map();

  /** Currently playing music key (for crossfade) */
  private currentMusic: string | null = null;
  private currentMusicObj: any = null;

  constructor(game: any) {
    this.game = game;
  }

  // --- Volume ---

  setMasterVolume(vol: number): void {
    this.masterVolume = Math.max(0, Math.min(1, vol));
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  setVolume(category: AudioCategory, vol: number): void {
    this.categoryVolumes[category] = Math.max(0, Math.min(1, vol));
  }

  getVolume(category: AudioCategory): number {
    return this.categoryVolumes[category];
  }

  private effectiveVolume(category: AudioCategory, baseVol: number): number {
    if (this.muted || this.categoryMuted[category]) return 0;
    return baseVol * this.categoryVolumes[category] * this.masterVolume;
  }

  // --- Mute ---

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  toggleCategoryMute(category: AudioCategory): boolean {
    this.categoryMuted[category] = !this.categoryMuted[category];
    return this.categoryMuted[category];
  }

  // --- Procedural tones ---

  /** Register a named procedural tone for later playback. */
  registerTone(key: string, config: ToneConfig): void {
    this.tones.set(key, config);
  }

  /** Play a registered procedural tone. pitchOffset shifts frequency (Hz). */
  playTone(key: string, pitchOffset = 0): void {
    const config = this.tones.get(key);
    if (!config) return;

    const vol = this.effectiveVolume("sfx", config.volume ?? 0.3);
    if (vol <= 0) return;

    if (pitchOffset === 0) {
      this.playToneRaw(config, vol);
    } else {
      this.playToneRaw({
        ...config,
        frequency: config.frequency + pitchOffset,
        slideTo: config.slideTo !== undefined ? config.slideTo + pitchOffset : undefined,
      }, vol);
    }
  }

  /** Play a one-off procedural tone without registering it. */
  playToneRaw(config: ToneConfig, overrideVol?: number): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const vol = overrideVol ?? this.effectiveVolume("sfx", config.volume ?? 0.3);
    if (vol <= 0) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = config.type ?? "sine";
    osc.frequency.setValueAtTime(config.frequency, ctx.currentTime);

    if (config.slideTo !== undefined) {
      osc.frequency.linearRampToValueAtTime(config.slideTo, ctx.currentTime + config.duration);
    }

    // Quick attack, sustain, then fade out to avoid clicks
    const fadeTime = Math.min(config.duration * 0.3, 0.05);
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.005);
    gain.gain.setValueAtTime(vol, ctx.currentTime + config.duration - fadeTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + config.duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + config.duration);
  }

  // --- Phaser sound playback (for loaded audio files) ---

  /** Play a loaded sound as SFX (one-shot). */
  playSfx(key: string, volume = 1): void {
    const vol = this.effectiveVolume("sfx", volume);
    if (vol <= 0) return;
    this.game.sound.play(key, { volume: vol });
  }

  /** Play a loaded sound as looping music. Stops current music. */
  playMusic(key: string, volume = 1): void {
    if (this.currentMusic === key) return;
    this.stopMusic();

    const vol = this.effectiveVolume("music", volume);
    this.currentMusicObj = this.game.sound.add(key, { volume: vol, loop: true });
    this.currentMusicObj.play();
    this.currentMusic = key;
  }

  /** Stop current music. */
  stopMusic(): void {
    if (this.currentMusicObj) {
      this.currentMusicObj.stop();
      this.currentMusicObj.destroy();
      this.currentMusicObj = null;
      this.currentMusic = null;
    }
  }

  // --- Internals ---

  /**
   * Use Phaser's Web Audio context — it handles unlocking on first user
   * interaction automatically. Creating our own AudioContext would start
   * suspended and never play until a separate user gesture unlocks it.
   */
  private getAudioContext(): AudioContext | null {
    try {
      const ctx = this.game.sound?.context as AudioContext | undefined;
      return ctx ?? null;
    } catch {
      return null;
    }
  }
}
